/**
 * D454: the tree on screen, shared by the Almanac (this year's steps) and the Festival tab's
 * permanent tree. A switcher of four branches, then the chosen branch as tiers, each a header
 * and a three-column grid of step tiles; a fork is two adjacent cells with "or" between. Tapping
 * a tile opens its sheet. The words come from `tree-view.ts`; this file only draws them.
 */
import type { ComponentChildren, JSX } from 'preact';
import { createContext } from 'preact';
import { useContext, useState } from 'preact/hooks';
import { BtnLabel, BuyButton, Figs } from './BuyButton.tsx';
import { Icon } from '../ui/Icon.tsx';
import { Sheet } from '../ui/Sheet.tsx';
import { IconTile, LockGlyph } from './tab-ui.tsx';
import type {
  TreeBranch,
  TreeMastery,
  TreeModel,
  TreeNext,
  TreeSheet,
  TreeTier,
  TreeTile,
} from './tree-view.ts';
import { hideBought, hideTopLevel, splitFact, type TierView } from './tree-view.ts';

const TONE: Record<TreeTile['tone'], string> = {
  accent: 'var(--accent)',
  green: 'var(--green)',
  ink: 'var(--ink)',
  muted: 'var(--ink-2)',
};

const BAR_TRACK: JSX.CSSProperties = {
  height: 3,
  borderRadius: 2,
  background: 'var(--surface-2)',
  overflow: 'hidden',
};

function Bar({ value, label }: { value: number; label: string }) {
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.floor(value * 100)}
      style={BAR_TRACK}
    >
      <div
        style={{
          width: `${Math.round(value * 100)}%`,
          height: '100%',
          background: 'var(--accent)',
          borderRadius: 2,
        }}
      />
    </div>
  );
}

/** D460: the steps marked as where a badge leads, for their rings and dots. */
const MarkedSteps = createContext<ReadonlySet<string>>(new Set());

/** The four branches as equal buttons: the name over `4 of 21`, a 3px bar along the foot. */
export function BranchSwitcher({
  branches,
  value,
  onChange,
  testId,
  marked = [],
}: {
  branches: TreeBranch[];
  value: string;
  onChange: (id: string) => void;
  testId: string;
  /** D460: branches holding a marked step, each with an 8px dot at its top right. */
  marked?: readonly string[];
}) {
  return (
    <div
      role="tablist"
      data-testid={testId}
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${branches.length}, minmax(0, 1fr))`,
        gap: 6,
      }}
    >
      {branches.map((b) => {
        const on = b.id === value;
        return (
          <button
            key={b.id}
            type="button"
            role="tab"
            aria-selected={on}
            data-testid={`${testId}-${b.id}`}
            onClick={() => onChange(b.id)}
            style={{
              position: 'relative',
              height: 52,
              minWidth: 0,
              padding: '6px 6px 0',
              border: 0,
              borderRadius: 'var(--radius-md)',
              // The chosen branch is a primary button, the rest secondary.
              background: on ? 'var(--accent)' : 'var(--surface)',
              boxShadow: on ? 'none' : 'inset 0 0 0 1px var(--line)',
              color: on ? 'var(--accent-ink)' : 'var(--ink)',
              cursor: 'pointer',
              display: 'grid',
              gridTemplateRows: '1fr auto',
              justifyItems: 'center',
              alignItems: 'center',
              overflow: 'hidden',
            }}
          >
            <span style={{ display: 'grid', gap: 3, justifyItems: 'center', minWidth: 0 }}>
              <span
                style={{
                  font: `${on ? 700 : 600} var(--fs-caption)/1.1 var(--font-ui)`,
                  whiteSpace: 'nowrap',
                }}
              >
                {b.name}
              </span>
              <span
                class="num"
                style={{
                  font: '500 var(--fs-badge)/1 var(--font-num)',
                  color: on ? 'var(--accent-ink)' : 'var(--ink-2)',
                  whiteSpace: 'nowrap',
                }}
              >
                {b.count}
              </span>
            </span>
            {marked.includes(b.id) && (
              <span
                class={on ? 'idle-waiting-dot idle-waiting-on-fill' : 'idle-waiting-dot'}
                data-testid={`${testId}-${b.id}-dot`}
                aria-hidden="true"
              />
            )}
            <span
              style={{
                ...BAR_TRACK,
                width: 'calc(100% + 12px)',
                borderRadius: 0,
                background: on
                  ? 'color-mix(in srgb, var(--accent-ink) 30%, transparent)'
                  : BAR_TRACK.background,
              }}
            >
              <span
                style={{
                  display: 'block',
                  width: `${Math.round(b.progress * 100)}%`,
                  height: '100%',
                  background: on ? 'var(--accent-ink)' : 'var(--accent)',
                }}
              />
            </span>
          </button>
        );
      })}
    </div>
  );
}

const HATCH =
  'repeating-linear-gradient(135deg, transparent 0 5px, color-mix(in srgb, var(--ink-2) 22%, transparent) 5px 6px)';

function tileStyle(tile: TreeTile, selected: boolean): JSX.CSSProperties {
  const base: JSX.CSSProperties = {
    position: 'relative',
    minHeight: 104,
    minWidth: 0,
    width: '100%',
    boxSizing: 'border-box',
    padding: 8,
    borderRadius: 'var(--radius-lg)',
    display: 'grid',
    gridTemplateRows: 'auto 1fr auto',
    gap: 6,
    textAlign: 'left',
    font: 'inherit',
    color: 'var(--ink)',
    cursor: 'pointer',
    border: '1px solid var(--line)',
    background: 'var(--surface)',
  };
  if (tile.state === 'bought')
    base.background = 'color-mix(in srgb, var(--accent) 8%, var(--surface))';
  else if (tile.state === 'open') {
    base.border = '1px solid var(--accent)';
    base.boxShadow = 'inset 0 0 0 1px var(--accent)';
  } else if (tile.state === 'locked') {
    base.background = 'transparent';
    base.border = '1px dashed var(--ink-2)';
  } else if (tile.state === 'closed') {
    base.background = HATCH;
    base.border = '1px dashed var(--ink-2)';
  }
  if (selected) base.outline = '2px solid var(--accent)';
  return base;
}

/** One step: a 30px icon, the effect in up to three lines, and its foot line. */
export function StepTile({
  tile,
  selected,
  onOpen,
  side,
}: {
  tile: TreeTile;
  selected: boolean;
  onOpen: (id: string) => void;
  /** In a fork: the edge that faces the "or" chip keeps clear space. */
  side?: 'left' | 'right';
}) {
  const closed = tile.state === 'closed';
  const marked = useContext(MarkedSteps).has(tile.id);
  return (
    <button
      type="button"
      class={marked ? 'idle-waiting' : undefined}
      data-testid={`step-${tile.id}`}
      data-state={tile.state}
      aria-label={`${tile.text}. ${tile.foot}`}
      onClick={() => onOpen(tile.id)}
      style={{
        ...tileStyle(tile, selected),
        ...(marked ? { border: '1px solid var(--accent)' } : {}),
        // The chip is 18px over a 6px gap: 6px into each tile, so 12px clear of its text.
        ...(side === 'left' ? { paddingRight: 18 } : side === 'right' ? { paddingLeft: 18 } : {}),
      }}
    >
      {marked && <span class="idle-waiting-dot idle-waiting-edge" aria-hidden="true" />}
      <span style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <span
          aria-hidden="true"
          style={{
            width: 30,
            height: 30,
            display: 'grid',
            placeItems: 'center',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--surface-2)',
            color: tile.state === 'locked' || closed ? 'var(--ink-2)' : 'var(--ink)',
          }}
        >
          {tile.state === 'locked' ? <LockGlyph size={16} /> : <Icon name={tile.icon} size={18} />}
        </span>
        {tile.state === 'bought' && (
          <span
            aria-hidden="true"
            style={{
              width: 'var(--chip-h)',
              height: 'var(--chip-h)',
              borderRadius: 9,
              background: 'var(--accent)',
              color: 'var(--accent-ink)',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <Icon name="check" size={12} stroke={3} />
          </span>
        )}
      </span>
      <span style={{ display: 'grid', alignContent: 'start', gap: 2, minWidth: 0 }}>
        <span
          style={{
            font: '600 var(--fs-caption)/1.25 var(--font-ui)',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflowWrap: 'anywhere',
            color: closed ? 'var(--ink-2)' : 'var(--ink)',
            textDecoration: closed ? 'line-through' : 'none',
          }}
        >
          {tile.text}
        </span>
        {tile.level && (
          <span style={{ font: '500 var(--fs-badge)/1.2 var(--font-ui)', color: 'var(--ink-2)' }}>
            <Figs text={tile.level} />
          </span>
        )}
      </span>
      <span
        class={tile.tone === 'accent' || /^\d/.test(tile.foot) ? 'num' : undefined}
        style={{
          font:
            tile.tone === 'accent'
              ? '600 var(--fs-note)/1.2 var(--font-num)'
              : '500 var(--fs-note)/1.25 var(--font-ui)',
          color: TONE[tile.tone],
          overflowWrap: 'anywhere',
        }}
      >
        {tile.foot}
      </span>
    </button>
  );
}

/** A tier's tiles: a fork is two adjacent cells with "or" between them. */
function TierGrid({
  tier,
  selected,
  onOpen,
}: {
  tier: TreeTier;
  selected: string | null;
  onOpen: (id: string) => void;
}) {
  const cells: JSX.Element[] = [];
  for (let i = 0; i < tier.tiles.length; i++) {
    const tile = tier.tiles[i]!;
    const mate = tile.fork !== undefined ? tier.tiles[i + 1] : undefined;
    if (mate && mate.fork === tile.fork) {
      cells.push(
        <div
          key={`${tile.id}-${mate.id}`}
          data-testid={`fork-${tile.fork}`}
          style={{
            gridColumn: 'span 2',
            position: 'relative',
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
            gap: 6,
          }}
        >
          <StepTile tile={tile} selected={selected === tile.id} onOpen={onOpen} side="left" />
          <StepTile tile={mate} selected={selected === mate.id} onOpen={onOpen} side="right" />
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: 'var(--chip-h)',
              height: 'var(--chip-h)',
              borderRadius: 9,
              background: 'var(--surface)',
              boxShadow: 'inset 0 0 0 1px var(--ink-2)',
              color: 'var(--ink-2)',
              font: '600 var(--fs-note)/18px var(--font-ui)',
              textAlign: 'center',
              pointerEvents: 'none',
            }}
          >
            or
          </span>
        </div>,
      );
      i++;
    } else {
      cells.push(
        <StepTile key={tile.id} tile={tile} selected={selected === tile.id} onOpen={onOpen} />,
      );
    }
  }
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gridAutoFlow: 'row dense',
        gap: 6,
      }}
    >
      {cells}
    </div>
  );
}

/** One tier: "Tier 2" and its state over its grid; a shut tier shows its progress bar. */
export function TierBlock({
  tier,
  branchName,
  selected,
  onOpen,
  view,
}: {
  tier: TreeTier;
  /** Shown over the header, so a scrolled view still says which branch it is. */
  branchName?: string;
  selected: string | null;
  onOpen: (id: string) => void;
  /** D471: the Almanac hides bought steps; absent, every step shows. */
  view?: TierView;
}) {
  const collapsed = view?.collapsed ?? false;
  const shown: TreeTier = view ? { ...tier, tiles: view.tiles } : tier;
  const kicker = branchName ? `${branchName} · tier ${tier.tier}` : `Tier ${tier.tier}`;
  return (
    <section
      data-testid={`tier-${tier.tier}`}
      data-shut={tier.shut ? 'true' : 'false'}
      style={{ display: 'grid', gap: 6 }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 8,
          margin: '4px 2px 0',
        }}
      >
        <span style={{ minWidth: 0 }}>
          {/* D459: one line, the kicker; the bold "Tier N" heading went (it cost 24px a tier). */}
          <h3
            data-testid={`tier-${tier.tier}-kicker`}
            style={{
              margin: 0,
              font: '600 var(--fs-note)/1.2 var(--font-ui)',
              color: 'var(--ink-2)',
            }}
          >
            {collapsed ? `${kicker} · ${view?.label ?? ''}` : kicker}
          </h3>
        </span>
        <span
          hidden={collapsed}
          data-testid={`tier-${tier.tier}-status`}
          style={{
            font: '500 var(--fs-note)/1.25 var(--font-ui)',
            color: 'var(--ink-2)',
            textAlign: 'right',
            minWidth: 0,
          }}
        >
          <Figs text={tier.status} />
        </span>
      </div>
      {tier.progress !== undefined && <Bar value={tier.progress} label={`${tier.head} opens`} />}
      {!collapsed && shown.tiles.length > 0 && (
        <TierGrid tier={shown} selected={selected} onOpen={onOpen} />
      )}
      {!collapsed && tier.forkNote && (
        <span
          style={{
            font: '400 var(--fs-caption)/1.35 var(--font-ui)',
            color: 'var(--ink-2)',
            margin: '0 2px',
          }}
        >
          {tier.forkNote}
        </span>
      )}
    </section>
  );
}

/**
 * D457: the mastery step at a branch's foot: a full-width tile in the money colour's tint, the
 * coins icon, its name over `level 4 · …`, and a 96px button for the next level.
 */
export function MasteryTile({
  mastery,
  onBuy,
}: {
  mastery: TreeMastery;
  onBuy: (el: Element) => void;
}) {
  const shut = mastery.price === null;
  return (
    <section data-testid={`mastery-${mastery.branch}`} style={{ display: 'grid', gap: 6 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 8,
          margin: '4px 2px 0',
        }}
      >
        <span style={{ font: '600 var(--fs-note)/1.2 var(--font-ui)', color: 'var(--ink-2)' }}>
          {mastery.kicker}
        </span>
        <span style={{ font: '500 var(--fs-note)/1.25 var(--font-ui)', color: 'var(--ink-2)' }}>
          No top
        </span>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: 10,
          borderRadius: 'var(--radius-lg)',
          border: shut ? '1px dashed var(--ink-2)' : '1px solid var(--coin-edge)',
          boxShadow: shut ? 'none' : 'inset 0 0 0 1px var(--coin-edge)',
          background: shut ? 'transparent' : 'color-mix(in srgb, var(--coin) 12%, var(--surface))',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 30,
            height: 30,
            flex: 'none',
            display: 'grid',
            placeItems: 'center',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--surface-2)',
            color: shut ? 'var(--ink-2)' : 'var(--ink)',
          }}
        >
          {shut ? <LockGlyph size={16} /> : <Icon name="coins" size={18} />}
        </span>
        <span style={{ flex: 1, minWidth: 0, display: 'grid', gap: 2 }}>
          <b style={{ font: '600 var(--fs-body)/1.25 var(--font-ui)' }}>{mastery.title}</b>
          <span
            data-testid={`mastery-${mastery.branch}-line`}
            style={{ font: '400 var(--fs-caption)/1.3 var(--font-ui)', color: 'var(--ink-2)' }}
          >
            <Figs text={mastery.line} />
          </span>
          {shut && mastery.off && (
            <span
              data-testid={`mastery-${mastery.branch}-off`}
              style={{ font: '500 var(--fs-note)/1.25 var(--font-ui)', color: 'var(--ink-2)' }}
            >
              {mastery.off}
            </span>
          )}
        </span>
        {!shut && (
          <BuyButton
            width={96}
            needs={mastery.off}
            label={
              mastery.off
                ? `${mastery.action}, ${mastery.off}`
                : `${mastery.action}, ${mastery.price}`
            }
            testId={`mastery-${mastery.branch}-buy`}
            onClick={(e) => onBuy(e.currentTarget as Element)}
          >
            <BtnLabel action={mastery.action} price={mastery.price} />
          </BuyButton>
        )}
      </div>
    </section>
  );
}

/** D457: the dashed card for the tier the next generation opens. */
function NextTierCard({ next }: { next: TreeNext }) {
  return (
    <div
      data-testid="next-tier"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        border: '1px dashed var(--line)',
        borderRadius: 'var(--radius-lg)',
        font: '400 var(--fs-caption)/1.35 var(--font-ui)',
        color: 'var(--ink)',
      }}
    >
      <span aria-hidden="true" style={{ color: 'var(--ink-2)', flex: 'none', display: 'grid' }}>
        <LockGlyph size={14} />
      </span>
      <span style={{ display: 'grid', gap: 2, minWidth: 0 }}>
        <b style={{ font: '600 var(--fs-body)/1.25 var(--font-ui)' }}>{next.title}</b>
        <Figs text={next.text} />
      </span>
    </div>
  );
}

/** The branch chosen: its tiers, in order, then its mastery step and the tier still to come. */
export function BranchBody({
  branch,
  selected,
  onOpen,
  onMastery,
  hide,
}: {
  branch: TreeBranch;
  selected: string | null;
  onOpen: (id: string) => void;
  onMastery?: ((branch: string, el: Element) => void) | undefined;
  /** D471: hide bought steps (D472: perks at their top level); `reveal` shows them again for this visit. */
  hide?: { reveal: boolean; onToggle: () => void; top?: boolean };
}) {
  const hidden = hide
    ? hide.top
      ? hideTopLevel(branch.tiers, hide.reveal)
      : hideBought(branch.tiers, hide.reveal)
    : undefined;
  const hiddenCount = hidden ? ('top' in hidden ? hidden.top : hidden.bought) : 0;
  const word = hide?.top ? 'top level' : 'bought';
  return (
    <div style={{ display: 'grid', gap: 8 }} data-testid={`branch-${branch.id}`}>
      {branch.tiers.map((t, i) => (
        <TierBlock
          key={t.tier}
          tier={t}
          {...(hidden ? { view: hidden.tiers[i]! } : {})}
          branchName={branch.name}
          selected={selected}
          onOpen={onOpen}
        />
      ))}
      {branch.mastery && onMastery && (
        <MasteryTile
          mastery={branch.mastery}
          onBuy={(el) => onMastery(branch.mastery!.branch, el)}
        />
      )}
      {branch.next && <NextTierCard next={branch.next} />}
      {hide && hidden && hiddenCount > 0 && (
        <button
          type="button"
          data-testid={hide.top ? 'show-top-level' : 'show-bought'}
          aria-expanded={hide.reveal}
          onClick={hide.onToggle}
          style={{
            width: '100%',
            minHeight: 44,
            padding: '0 12px',
            border: '1px dashed var(--line)',
            borderRadius: 12,
            background: 'transparent',
            color: 'var(--accent)',
            font: '600 var(--fs-label)/1.2 var(--font-ui)',
            cursor: 'pointer',
          }}
        >
          {hide.reveal ? `Hide ${word}` : `Show ${word} (${hiddenCount})`}
        </button>
      )}
    </div>
  );
}

/**
 * The step sheet: the icon, a kicker (`Production · tier 2`), the effect as the title, ruled
 * lines of what it changes, then Buy with its price full width, disabled with its Needs line,
 * or the words for what opens it.
 */
export function StepSheet({
  sheet,
  onClose,
  onBuy,
  testId,
}: {
  sheet: TreeSheet;
  onClose: () => void;
  onBuy: (el: Element) => void;
  testId: string;
}) {
  return (
    <Sheet
      align="bottom"
      testId={testId}
      ariaLabel={sheet.title}
      onClose={onClose}
      zIndex={40}
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <IconTile name={sheet.icon} size={44} icon={22} />
          <span style={{ display: 'grid', gap: 2, minWidth: 0 }}>
            <span
              data-testid={`${testId}-kicker`}
              style={{ font: '600 var(--fs-caption)/1.2 var(--font-ui)', color: 'var(--ink-2)' }}
            >
              {sheet.kicker}
            </span>
            <span style={{ font: '600 var(--fs-title)/1.3 var(--font-ui)' }}>{sheet.title}</span>
          </span>
        </span>
      }
    >
      {sheet.lines.length > 0 && (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none' }} data-testid={`${testId}-lines`}>
          {sheet.lines.map((l) => {
            const f = splitFact(l);
            return (
              <li
                key={l}
                data-fact={'label' in f ? 'pair' : 'note'}
                style={{
                  padding: '9px 0',
                  borderTop: '1px solid var(--line)',
                  font: '400 var(--fs-label)/1.35 var(--font-ui)',
                  display: 'grid',
                  gridTemplateColumns: 'label' in f ? 'minmax(0, 1fr) auto' : 'minmax(0, 1fr)',
                  gap: 12,
                }}
              >
                {'label' in f ? (
                  <>
                    <span style={{ color: 'var(--ink-2)' }}>{f.label}</span>
                    <span style={{ fontWeight: 600, textAlign: 'right' }}>
                      <Figs text={f.value} />
                    </span>
                  </>
                ) : (
                  <span style={{ color: 'var(--ink-2)' }}>
                    <Figs text={f.note} />
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {sheet.done ? (
        <span
          style={{ font: '600 var(--fs-label)/1.3 var(--font-ui)', color: 'var(--green)' }}
          data-testid={`${testId}-done`}
        >
          {sheet.done}
        </span>
      ) : null}
      {sheet.price !== null && (
        <BuyButton
          fill
          needs={sheet.off}
          label={sheet.off ? `Buy, ${sheet.off}` : `Buy, ${sheet.price}`}
          testId={`${testId}-buy`}
          onClick={(e) => onBuy(e.currentTarget as Element)}
        >
          <BtnLabel action="Buy" price={sheet.price} />
        </BuyButton>
      )}
      {sheet.price === null && sheet.off && (
        <span style={{ font: '400 var(--fs-label)/1.35 var(--font-ui)', color: 'var(--ink-2)' }}>
          {sheet.off}
        </span>
      )}
    </Sheet>
  );
}

/**
 * The pinned group at the top of a tree page: the switcher (and the branch chips under it). The
 * Almanac's Goals page pins its switcher in the same group, so changing page moves nothing.
 */
export const PINNED_HEAD: JSX.CSSProperties = {
  position: 'sticky',
  // The tab body pads 8px above its content; the group pins flush to the top edge.
  top: -8,
  zIndex: 3,
  display: 'grid',
  gap: 8,
  padding: '8px 0',
  background: 'var(--surface)',
  borderBottom: '1px solid var(--line)',
};

/** The branch last looked at, kept for the visit, so leaving a page and coming back keeps its place. */
const lastBranch: Record<string, string> = {};

/**
 * A whole tree: the switcher, the chosen branch and the step sheet. `buy` makes the purchase and
 * says whether it went through; the sheet closes when it did.
 */
export function TreePanel({
  model,
  testId,
  buy,
  above,
  head,
  mastery,
  marks,
}: {
  model: TreeModel;
  testId: string;
  buy: (id: string, el: Element) => boolean;
  /** D457: buy a branch's next mastery level (the Almanac only). */
  mastery?: (branch: string, el: Element) => void;
  /** Anything that sits above the branches, such as the one-time note. */
  above?: ComponentChildren;
  /** Sits over the switcher in the same sticky group, such as the Tree and Goals control. */
  head?: ComponentChildren;
  /** D460: the steps a badge leads to, and the branches that hold them. */
  marks?: { items: readonly string[]; branches: readonly string[] };
}) {
  // D460: arriving with marked steps opens a branch holding one, unless the last one does.
  const [branchId, setBranch] = useState(() => {
    const last = lastBranch[testId];
    const firstMarked = marks?.branches[0];
    if (firstMarked && !(last && marks.branches.includes(last))) return firstMarked;
    return last ?? model.branches[0]?.id ?? '';
  });
  const markedSteps = new Set(marks?.items ?? []);
  const [open, setOpen] = useState<string | null>(null);
  const [reveal, setReveal] = useState(false);
  const branch = model.branches.find((b) => b.id === branchId) ?? model.branches[0];
  if (!branch) return null;
  const sheet = open ? model.sheets[open] : undefined;
  return (
    <div style={{ display: 'grid', gap: 8 }} data-testid={testId}>
      {above}
      <div data-testid={`${testId}-sticky`} style={PINNED_HEAD}>
        {head}
        <BranchSwitcher
          branches={model.branches}
          value={branch.id}
          testId={`${testId}-branches`}
          marked={marks?.branches}
          onChange={(id) => {
            lastBranch[testId] = id;
            setBranch(id);
          }}
        />
      </div>
      <MarkedSteps.Provider value={markedSteps}>
        <BranchBody
          branch={branch}
          selected={sheet ? sheet.id : null}
          onOpen={setOpen}
          onMastery={mastery}
          hide={{ reveal, onToggle: () => setReveal(!reveal), top: !mastery }}
        />
      </MarkedSteps.Provider>
      {sheet && (
        <StepSheet
          sheet={sheet}
          testId={`${testId}-sheet`}
          onClose={() => setOpen(null)}
          onBuy={(el) => {
            if (buy(sheet.id, el)) setOpen(null);
          }}
        />
      )}
    </div>
  );
}
